/**
 * Map rail badge for SO-08 trades (eager, tiny: the manifest imports it). The chunk stores the
 * local expiry times of pending incoming offers whenever it loads the trade list; the badge
 * counts the ones still open, so it needs no request.
 */
import type { AppContext } from '../../game/context.js';
import type { KV } from '../../services/storage.js';

const PENDING = 'trading:pending';
const SEEN = 'trading:seen';

function readNumbers(kv: KV, key: string): number[] {
  try {
    const v = JSON.parse(kv.get(key) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

/**
 * Trades swap album stickers: with the album switched off (`collections` flag; GET
 * /v1/collections answers 403) there is nothing to pick from, so trading counts as off too.
 * The manifest has one flag, so the map hook and badge check this one here.
 */
export function albumOff(a: Pick<AppContext, 'remote'>): boolean {
  return a.remote.flags.collections === false;
}

/** Incoming offers still waiting for an answer (the rail badge). */
export function pendingIncoming(kv: KV, now: number): number {
  return readNumbers(kv, PENDING).filter((expiresAt) => expiresAt > now).length;
}

export function setPendingIncoming(kv: KV, localExpiries: readonly number[]): void {
  kv.set(PENDING, JSON.stringify(localExpiries));
}

/** Offer ids the player already saw (the map-show notice shows each offer once). */
export function readSeen(kv: KV): string[] {
  try {
    const v = JSON.parse(kv.get(SEEN) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function markSeen(kv: KV, ids: readonly string[]): void {
  if (!ids.length) return;
  kv.set(SEEN, JSON.stringify([...new Set([...readSeen(kv), ...ids])].slice(-100)));
}
